import { siqBaseUrl } from "@/constants";
import { selectLeaseComps } from "@/features/survey/leaseCompsSelectors";
import {
  setCurrentLeaseCompId,
  setOpenLeaseCompsDrawer,
} from "@/features/survey/leaseCompsSlice";
import { selectPOIById, selectPOIs } from "@/features/survey/poisSelectors";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import SkeletonLoader from "@components/SkeletonLoader";
import { EmeraldButton } from "@emerald-react/button";
import { EmeraldIconButton } from "@emerald-react/icon-button";
import { EmeraldInput } from "@emerald-react/input";
import { EmeraldSideSheet } from "@emerald-react/side-sheet";
import { EmeraldSidesheetPositionType } from "@emerald/nxcore";
import { usePoiCategoryWarning } from "@hooks/usePoiCategoryWarning";
import {
  useCurrentPoiId,
  useCurrentPropertyId,
  useIsPOIsDrawerOpen,
  useIsPropertiesDrawerOpen,
} from "@hooks/useSurveySelectors";
import useSurveyState from "@hooks/useSurveyState";
import useSurveyUpdate from "@hooks/useSurveyUpdate";
import { surveyCoreApi } from "@services/survey-core";
import { StorageUtil } from "@shared/utils";
import Analytics from "@shared/utils/tagManagerAnalytics";
import PropertiesListDrawer from "@survey-properties/PropertiesListDrawer";
import PropertyDetailForm from "@survey-properties/PropertyDetailForm";
import LeaseCompDetailForm from "@survey/components/builder/LeaseCompDetailForm";
import LeaseCompsListDrawer from "@survey/components/builder/LeaseCompsListDrawer";
import PoiDetailForm from "@survey/components/builder/PoiDetailForm";
import PoisListDrawer from "@survey/components/builder/PoisListDrawer";
import {
  currentPoiId as currentPoiIdAction,
  currentPropertyId as currentPropertyIdAction,
  setOpenPOIsDrawer,
  setOpenPropertiesDrawer,
} from "@survey/surveySlice";
import { isUSUser } from "@utils/countryUtils";
import { BaseSyntheticEvent } from "react";
import { useParams } from "react-router";

const LoadingSkeleton = () => (
  <header className="builder-header">
    <div className="flex gap-x-2 items-center">
      <SkeletonLoader className="w-9 h-10" />
      <SkeletonLoader className="w-64 h-10" />
    </div>
    <div>
      <SkeletonLoader className="w-42 h-10" />
    </div>
  </header>
);

const BuilderHeader = () => {
  const { surveyId } = useParams();
  // Use the reusable survey state hook
  const { survey } = useSurveyState();

  // Survey name handling with shared hook
  const {
    value: surveyName,
    setValue: setSurveyName,
    triggerSave: saveSurveyName,
  } = useSurveyUpdate("name", survey?.name || "Untitled Survey", {
    transformBeforeSave: (value: string) => {
      const trimmedValue = value.trim();
      return trimmedValue === "" ? "Untitled Survey" : trimmedValue;
    },
    onSaveError: (_error, originalValue) => {
      console.error("Failed to update survey name");
      setSurveyName(originalValue);
    },
  });

  const currentPropertyId = useCurrentPropertyId();
  const currentPoiId = useCurrentPoiId();
  const isPropertiesDrawerOpen = useIsPropertiesDrawerOpen();
  const isPOIsDrawerOpen = useIsPOIsDrawerOpen();
  const isLeaseCompsDrawerOpen = useAppSelector(
    (state) => state.leaseComps.isDrawerOpen,
  );
  const currentLeaseCompId = useAppSelector(
    (state) => state.leaseComps.currentLeaseCompId,
  );
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector((state) => state.survey.isLoading);

  const leaseComps = useAppSelector(selectLeaseComps);
  const leaseCompsCount = leaseComps.length;
  const poisCount = useAppSelector(selectPOIs).length;

  // Get current POI for category validation
  const currentPOI = useAppSelector((state) =>
    selectPOIById(currentPoiId)(state),
  );

  // Use shared hook for POI category warning logic
  const {
    showCategoryWarning,
    confirmCategoryWarning,
    checkAndDeletePOIWithoutCategory,
  } = usePoiCategoryWarning();

  const handleSurveyNameChange = (e: BaseSyntheticEvent) => {
    setSurveyName(e.target.value);
  };

  const handleInputFocus = (e: BaseSyntheticEvent) => {
    if (surveyName === "Untitled Survey") {
      e.target.select();
    }
  };

  const handleInputBlur = () => {
    saveSurveyName();
  };

  const handleOpenLeaseCompsDrawer = () => {
    dispatch(setOpenLeaseCompsDrawer(true));
    dispatch(setCurrentLeaseCompId(null));
  };

  const handleCloseLeaseCompsDrawer = () => {
    dispatch(setOpenLeaseCompsDrawer(false));
    dispatch(setCurrentLeaseCompId(null));
  };

  const goHome = () => {
    // redirect user to the SIQ when appMode is surveyBuilderOnly
    const appMode = StorageUtil.getSessionItem("appMode");
    const sc_surveyId = StorageUtil.getSessionItem("sc_surveyId");
    if (
      appMode &&
      appMode === "surveyBuilderOnly" &&
      sc_surveyId &&
      siqBaseUrl
    ) {
      const siq_project = StorageUtil.getSessionItem("siq_project");
      const safeProject =
        siq_project && siq_project !== "_" && /^[\w-]+$/.test(siq_project)
          ? encodeURIComponent(siq_project)
          : null;
      const destination = safeProject
        ? `${siqBaseUrl}projects/${safeProject}/deliverables`
        : siqBaseUrl;
      window.location.assign(destination);
    } else {
      if (survey?.projectId) {
        const destination =
          siqBaseUrl + "projects/" + survey?.projectId + "/deliverables";
        window.location.assign(destination);
      } else {
        window.location.assign(siqBaseUrl);
      }
    }
  };

  const handleOpenPropertiesDrawer = () => {
    // Send analytics event when property drawer opens
    if (survey?.id !== undefined) {
      Analytics.send({
        event: "userDataReady",
        user_id: localStorage.getItem("employeeId"),
        jobTitle: localStorage.getItem("position"),
        officeName: localStorage.getItem("office"),
        email: localStorage.getItem("email"),
        functUnitName: localStorage.getItem("functUnitName"),
        SiteIQUserID: survey?.createdBy || null, // Use createdBy as fallback since user property doesn't exist
        teamName: survey?.teamName, // team property not available on DetailedSurvey type
      });
    }
    dispatch(setOpenPropertiesDrawer(true));
    dispatch(currentPropertyIdAction(null));
  };

  const handleClosePropertiesDrawer = () => {
    dispatch(setOpenPropertiesDrawer(false));
    dispatch(currentPropertyIdAction(null));
    if (survey?.id) {
      dispatch(
        surveyCoreApi.util.invalidateTags([{ type: "Survey", id: survey.id }]),
      );
    }
  };

  const handleOpenPOIsDrawer = () => {
    dispatch(setOpenPOIsDrawer(true));
    dispatch(currentPoiIdAction(null));
  };

  const handleClosePOIsDrawer = async () => {
    // Check if we're closing a POI detail form without a category
    if (
      currentPoiId &&
      currentPOI &&
      surveyId &&
      showCategoryWarning(currentPOI.categoryId)
    ) {
      // Show warning dialog
      const userConfirmed = await confirmCategoryWarning();

      if (!userConfirmed) {
        // User clicked "Stay Here" - do nothing
        return;
      }

      // User clicked "Leave" - delete POI and close drawer
      await checkAndDeletePOIWithoutCategory(
        currentPOI.categoryId,
        surveyId,
        currentPOI.extId,
        currentPOI,
      );
    }

    // Close drawer and reset POI
    dispatch(setOpenPOIsDrawer(false));
    dispatch(currentPoiIdAction(null));
  };

  if (isLoading || (survey && survey.id !== surveyId)) {
    return <LoadingSkeleton />;
  }

  return (
    <>
      <header className="builder-header">
        <div className="flex gap-x-2 items-center">
          <EmeraldIconButton
            icon="keyboard_arrow_left"
            onClick={goHome}
            type="Icon"
          />
          <EmeraldInput
            value={surveyName}
            placeholder="Survey Name"
            className="w-64 emerald-standard"
            inputSize="small"
            outlined={false}
            onChange={handleSurveyNameChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
          />
        </div>
        <div>
          {survey && (
            <div className="flex gap-x-2">
              <EmeraldButton
                id="SC-OpenManageProperties"
                label={`Properties (${survey.properties?.length ?? 0})`}
                icon="business"
                onClick={handleOpenPropertiesDrawer}
              />
              {isUSUser() && (
                <EmeraldButton
                  id="SC-OpenLeaseComps"
                  label={`Lease Comps (${leaseCompsCount})`}
                  onClick={handleOpenLeaseCompsDrawer}
                />
              )}
              <EmeraldButton
                id="SC-OpenManagePOIs"
                label={`POIs (${poisCount})`}
                onClick={handleOpenPOIsDrawer}
              />
            </div>
          )}
        </div>
      </header>
      <EmeraldSideSheet
        opened={isPropertiesDrawerOpen}
        title={`${currentPropertyId ? "" : "Manage Properties"}`}
        position={"right" as EmeraldSidesheetPositionType}
        closeOnOutsideClick={true}
        hasCloseButton={!currentPropertyId && true}
        closeOnEscape={true}
        onClose={handleClosePropertiesDrawer}
        className="w-[75vw]! property-drawer-list"
      >
        {currentPropertyId ? <PropertyDetailForm /> : <PropertiesListDrawer />}
      </EmeraldSideSheet>
      <EmeraldSideSheet
        opened={isLeaseCompsDrawerOpen}
        title={`${currentLeaseCompId ? "" : "Manage Lease Comps"}`}
        position={"right" as EmeraldSidesheetPositionType}
        closeOnOutsideClick={true}
        hasCloseButton={!currentLeaseCompId && true}
        closeOnEscape={true}
        onClose={handleCloseLeaseCompsDrawer}
        className="w-[75vw]! lease-comps-drawer-list"
      >
        {currentLeaseCompId ? (
          <LeaseCompDetailForm />
        ) : (
          <LeaseCompsListDrawer />
        )}
      </EmeraldSideSheet>
      <EmeraldSideSheet
        opened={isPOIsDrawerOpen}
        title={`${currentPoiId ? "" : "Manage Points of Interest"}`}
        position={"right" as EmeraldSidesheetPositionType}
        closeOnOutsideClick={true}
        hasCloseButton={!currentPoiId && true}
        closeOnEscape={true}
        onClose={handleClosePOIsDrawer}
        className="w-[75vw]! poi-drawer-list"
      >
        {isPOIsDrawerOpen && (
          currentPoiId ? <PoiDetailForm /> : <PoisListDrawer />
        )}
      </EmeraldSideSheet>
    </>
  );
};

// Add displayName for better debugging
BuilderHeader.displayName = "BuilderHeader";

export default BuilderHeader;
